import { configureStore } from '@reduxjs/toolkit';
import {
  identifierReducer,
  variationReducer,
  authMiddleware,
  cartMiddleware,
  variationMiddleware,
  identifierMiddleware,
  scopedKey,
} from '@amzn/d2c-application-website-common';
import authReducer from 'src/store/authSlice';
import merchReducer from 'src/store/merchSlice';
import merchV2Reducer from 'src/store/merchV2Slice';
import catalogReducer from 'src/store/catalogSlice';
import cartReducer from 'src/store/cartSlice';
import templateReducer from 'src/store/templateSlice';

let _scopeKey = '';
let _scopeResolved = false;

export const getStorageScope = (): string => {
  if (!_scopeResolved) {
    _scopeResolved = true;
    try {
      const { getStage, getAsinStage } = require('src/utils/roadieHelper');
      const { getSite, getIsPreview, getVersion } = require('src/utils/siteUtils');
      const stage = getStage();
      const isPreview = getIsPreview();
      const env = isPreview ? 'PREVIEW' : 'LIVE';
      const siteId = getSite(stage, env) || '';
      const asinStage = getAsinStage();
      const version = getVersion(stage, env, isPreview);
      const parts = [siteId, asinStage, version].filter(Boolean);
      _scopeKey = parts.join('_');
    } catch {
      _scopeKey = '';
    }
  }
  return _scopeKey;
};

export const storageKey = (base: string): string => scopedKey(base, getStorageScope() || undefined);

const safeParse = (key: string) => {
  try {
    const raw = localStorage.getItem(storageKey(key));
    return raw ? JSON.parse(raw) : undefined;
  } catch (e) {
    console.error(`Failed to parse ${key} from localStorage:`, e);
    return undefined;
  }
};

const loadPersistedState = () => {
  const authState = safeParse('authState');
  const cartState = safeParse('cartState');
  const variationState = safeParse('variationState');
  const identifierState = safeParse('identifierState');

  return {
    ...(authState && { auth: authState }),
    ...(cartState && { cart: { ...cartReducer(undefined, { type: '' }), cart: cartState } }),
    ...(variationState && { variation: variationState }),
    ...(identifierState && { identifier: { sessionId: identifierState } }),
  };
};

const storageScope = getStorageScope() || undefined;

export const store = configureStore({
  reducer: {
    auth: authReducer,
    merch: merchReducer,
    merchV2: merchV2Reducer,
    catalog: catalogReducer,
    cart: cartReducer,
    variation: variationReducer,
    identifier: identifierReducer,
    template: templateReducer,
  },
  preloadedState: loadPersistedState(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      authMiddleware(storageScope),
      cartMiddleware(storageScope),
      variationMiddleware(storageScope),
      identifierMiddleware(storageScope),
    ]),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
